/**
 * Language Sync Middleware
 *
 * Records the signed-in cook's app language in
 * `users/{uid}/userPreferences/personal.language`, so the weekly email can be
 * written in it. The app kept the language on the device only (redux and the
 * `user_language` key), and the email came out in English for everyone.
 *
 * It fires on any transition of (uid, language): a language switch, the boot
 * that rehydrates a signed-in session, and a sign-in. A guest has no uid and is
 * never written. One write per value per session.
 */

import { createListenerMiddleware } from '@reduxjs/toolkit';
import { syncPersonalLanguageToCloud } from '../services/userPreferencesSyncService';
import { isFirebaseEnabled } from '../services/firebaseConfig';

type LanguageSyncState = {
  auth?: { user?: { uid?: string } | null };
  language?: { currentLanguage?: string };
};

const uidOf = (state: LanguageSyncState) => state.auth?.user?.uid ?? null;
const languageOf = (state: LanguageSyncState): 'en' | 'fr' | null => {
  const lang = state.language?.currentLanguage;
  return lang === 'en' || lang === 'fr' ? lang : null;
};

/**
 * The `uid:language` pair to record, or null when there is nothing new: a
 * guest, a language that is not one of ours, or the pair already written this
 * session. Pure; exported for tests.
 */
export function shouldRecordLanguage(state: LanguageSyncState, lastRecorded: string | null): string | null {
  const uid = uidOf(state);
  const lang = languageOf(state);
  if (!uid || !lang) return null;
  const key = `${uid}:${lang}`;
  return key === lastRecorded ? null : key;
}

export const languageSyncMiddleware = createListenerMiddleware();

let lastRecorded: string | null = null;

languageSyncMiddleware.startListening({
  predicate: (_action, current) => shouldRecordLanguage(current as LanguageSyncState, lastRecorded) !== null,
  effect: async (_action, listenerApi) => {
    if (!isFirebaseEnabled()) return;
    const state = listenerApi.getState() as LanguageSyncState;
    const key = shouldRecordLanguage(state, lastRecorded);
    if (!key) return;
    lastRecorded = key;
    try {
      await syncPersonalLanguageToCloud(uidOf(state)!, languageOf(state)!);
    } catch (error) {
      // Not fatal: the email falls back to guessing. Retried on the next session.
      lastRecorded = null;
      console.error('Failed to record the app language:', error);
    }
  },
});

/** Test hook: forget what was recorded this session. */
export function resetLanguageSyncForTests(): void {
  lastRecorded = null;
}

export default languageSyncMiddleware;
